/**
 * Body Scan — privacy consent → 2-photo capture → Gemini analysis → results.
 * Route: /health/body-scan
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Camera as CameraIcon, RefreshCw } from "lucide-react";
import TopBar from "../../components/layout/TopBar";
import BottomNav from "../../components/layout/BottomNav";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { toast } from "sonner";

export default function BodyScan() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [step, setStep] = useState("intro"); // intro | front | side | analyzing | result
  const [front, setFront] = useState(null);
  const [side, setSide] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [consent, setConsent] = useState(false);
  const fileRef = useRef(null);
  const [pickWhich, setPickWhich] = useState("front");

  useEffect(() => {
    apiClient.get("/body-scan/history?limit=10").then((r) => setHistory(r.data || [])).catch(() => {});
  }, []);

  const showConsent = !user?.body_scan_consent && !consent;

  const onFile = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = reader.result;
      // Pre-flight quality check
      try {
        const { data } = await apiClient.post("/body-scan/quality-check", { image_base64: b64 });
        if (!data?.ok) {
          toast.error(`${data?.reason || "Image quality is low"}. Please retake.`);
          return;
        }
      } catch {
        // ignore — proceed
      }
      if (pickWhich === "front") {
        setFront(b64);
        setStep("side");
        setPickWhich("side");
      } else {
        setSide(b64);
        setStep("analyzing");
        analyze(front, b64);
      }
    };
    reader.readAsDataURL(file);
  };

  const analyze = async (frontB64, sideB64) => {
    try {
      const { data } = await apiClient.post("/body-scan/analyze", {
        front_image_b64: frontB64,
        side_image_b64: sideB64,
        privacy_consent_given: true,
      });
      if (data?.error) {
        toast.error(data.reason || "Could not complete scan. Try in better conditions.");
        setStep("intro");
        return;
      }
      setResult(data);
      setStep("result");
      apiClient.get("/body-scan/history?limit=10").then((r) => setHistory(r.data || []));
    } catch {
      toast.error("Analysis failed. Please retry.");
      setStep("intro");
    }
  };

  const reset = () => {
    setFront(null);
    setSide(null);
    setResult(null);
    setStep("intro");
    setPickWhich("front");
  };

  return (
    <div className="app-page" data-testid="body-scan-page">
      <header className="sticky top-0 z-20 bg-canvas border-b border-line2">
        <div className="max-w-2xl mx-auto h-14 px-4 flex items-center gap-2">
          <button onClick={() => navigate(-1)} aria-label="Back" className="-ml-2 w-10 h-10 grid place-items-center">
            <ChevronLeft size={22} />
          </button>
          <h1 className="font-display text-[20px]">Body Scan</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-4 pb-32">
        {/* Privacy consent */}
        {showConsent && (
          <div className="bg-white rounded-[16px] p-5 border border-line2" data-testid="body-scan-consent">
            <h2 className="font-display text-[22px]">Your photos stay private</h2>
            <p className="text-[14px] text-ink-soft mt-2">
              Your scan photos are stored privately and are only visible to you. They are never shared or used for any other purpose.
            </p>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => {
                  setConsent(true);
                  setStep("intro");
                }}
                data-testid="consent-accept"
                className="flex-1 h-11 rounded-pill"
                style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700 }}
              >
                I understand, continue
              </button>
              <button onClick={() => navigate(-1)} className="flex-1 h-11 rounded-pill border border-line2 text-[13px]">
                Cancel
              </button>
            </div>
          </div>
        )}

        {!showConsent && step === "intro" && (
          <div data-testid="body-scan-intro">
            <h2 className="font-display text-[28px]">Photo body composition</h2>
            <p className="text-[14px] text-ink-soft mt-1">
              Take a front-facing and side-facing photo. AI will estimate your body fat range, posture, and muscle definition.
            </p>
            <button
              onClick={() => {
                setPickWhich("front");
                setStep("front");
                fileRef.current?.click();
              }}
              data-testid="start-scan"
              className="mt-5 w-full h-12 rounded-pill"
              style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700 }}
            >
              Start scan — Front photo
            </button>

            {history.length > 0 && (
              <section className="mt-8" data-testid="scan-history">
                <h3 className="font-display text-[18px] mb-2">Scan History</h3>
                <div className="space-y-2">
                  {history.map((h) => (
                    <div key={h.scan_id} className="bg-white rounded-[12px] p-3 border border-line2" data-testid={`scan-${h.scan_id}`}>
                      <div className="text-[13px] font-medium">
                        {(h.scan_date || "").slice(0, 10)}
                      </div>
                      <div className="text-[12px] text-ink-soft mt-0.5">
                        {h.body_fat_min ? `Body fat ${h.body_fat_min}–${h.body_fat_max}%` : "Measurements"} · {h.method}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {(step === "front" || step === "side") && !showConsent && (
          <div className="text-center" data-testid={`scan-step-${step}`}>
            <h2 className="font-display text-[26px]">
              {step === "front" ? "Front-facing photo" : "Side-facing photo"}
            </h2>
            <p className="text-[14px] text-ink-soft mt-1">
              Stand in plain background, good lighting, arms slightly out from body.
            </p>
            <div
              className="mt-5 mx-auto w-[80%] aspect-[3/5] rounded-[16px] border-2 border-dashed grid place-items-center text-ink-muted"
              style={{ borderColor: "var(--border)" }}
            >
              <div className="text-center px-4">
                <CameraIcon size={32} className="mx-auto opacity-60" />
                <div className="mt-2 text-[12px]">Stand here</div>
                <div className="mt-1 text-[10px]">Arms slightly out from body</div>
              </div>
            </div>
            <button
              onClick={() => {
                setPickWhich(step);
                fileRef.current?.click();
              }}
              data-testid={`take-${step}-photo`}
              className="mt-5 w-full h-12 rounded-pill"
              style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700 }}
            >
              {step === "front" ? "Take front photo" : "Take side photo"}
            </button>
          </div>
        )}

        {step === "analyzing" && (
          <div className="text-center mt-12" data-testid="scan-analyzing">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 rounded-full mx-auto"
              style={{ borderTop: "3px solid #00D4FF", borderRight: "3px solid transparent" }}
            />
            <h2 className="font-display text-[24px] mt-4">Analyzing</h2>
            <p className="text-[13px] text-ink-soft mt-1">This usually takes 5–10 seconds.</p>
          </div>
        )}

        {step === "result" && result && (
          <div className="space-y-4" data-testid="scan-result">
            <div className="bg-white rounded-[16px] p-5 border border-line2 text-center">
              <div className="text-[11px] uppercase tracking-wider text-ink-tertiary font-medium">
                Estimated Body Fat
              </div>
              <div
                className="font-barlow-cond mt-1"
                style={{ fontSize: 56, fontWeight: 900, lineHeight: 1, color: "var(--text-primary)" }}
              >
                {result.body_fat_min} — {result.body_fat_max}%
              </div>
              <div className="mt-2 inline-flex items-center gap-2">
                <span
                  className="px-3 py-1 rounded-pill capitalize text-[12px] font-medium"
                  style={{ background: "var(--accent-blue-dim)", color: "var(--text-primary)", borderRadius: 100 }}
                >
                  {result.muscle_definition} muscle definition
                </span>
                <span
                  className="px-3 py-1 rounded-pill capitalize text-[12px]"
                  style={{
                    background: result.confidence === "high" ? "var(--accent-blue-dim)" : "#FFF1D1",
                    color: result.confidence === "high" ? "var(--text-primary)" : "#A05E00",
                    borderRadius: 100,
                  }}
                >
                  {result.confidence} confidence
                </span>
              </div>
              {result.confidence_reason && (
                <div className="text-[11px] text-ink-soft mt-2 italic">
                  {result.confidence_reason}
                </div>
              )}
            </div>

            <div className="bg-white rounded-[16px] p-5 border border-line2">
              <h3 className="font-display text-[18px]">Posture</h3>
              <PostureRow label="Head" value={result.posture?.head_alignment || "—"} />
              <PostureRow label="Shoulders" value={result.posture?.shoulder_symmetry || "—"} />
              <PostureRow label="Hips" value={result.posture?.hip_alignment || "—"} />
            </div>

            {result.observations?.length > 0 && (
              <div className="bg-white rounded-[16px] p-5 border border-line2">
                <h3 className="font-display text-[18px]">Observations</h3>
                <ul className="mt-2 space-y-1.5">
                  {result.observations.map((o, i) => (
                    <li key={i} className="text-[13px] text-ink-soft">· {o}</li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-[11px] text-ink-tertiary text-center px-2">
              Photo estimates have approximately 4% variance. For clinical accuracy consult a qualified professional.
            </p>

            <button
              onClick={reset}
              data-testid="scan-again"
              className="w-full h-11 rounded-pill flex items-center justify-center gap-2 border border-line2"
              style={{ borderRadius: 100 }}
            >
              <RefreshCw size={14} /> Take another scan
            </button>
          </div>
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])}
          data-testid="body-scan-file"
        />
      </main>
      <BottomNav />
    </div>
  );
}

function PostureRow({ label, value }) {
  return (
    <div className="flex items-center justify-between mt-2">
      <span className="text-[13px] text-ink-soft">{label}</span>
      <span className="text-[13px] font-medium capitalize">{(value || "").replace(/_/g, " ")}</span>
    </div>
  );
}
